import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { formatDistanceToNow } from "date-fns"
import { IconTrash2Outline18 as Trash2 } from "nucleo-ui-outline-18"

import {
	AlertDialog,
	Button,
	DropdownMenu,
	DropdownMenuItem,
	IconButton,
} from "@productlane/ui"
import { EllipsisIcon as MoreHorizontal } from "@productlane/ui/src/icons/project/ellipsis-icon"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { SettingsContainer } from "../../../settings-switches/settings-container"
import { SettingsSection } from "../../../settings-switches/settings-section"

interface ConnectedApp {
	clientId: string
	clientName: string
	logoUri: string | null
	userId: string
	userName: string
	lastUsedAt: string | null
	grantedAt: string
}

export function ConnectedAppsBlock() {
	const { data: apps } = api.workspace.listConnectedApps.useQuery()

	return (
		<>
			<SettingsSection
				title="Connected apps"
				description={
					<>
						Apps authorized to access this workspace over MCP, one entry per
						person who connected.{" "}
						<a
							href="https://productlane.com/docs/935390b2-2de7-4757-a2eb-8bb7acba9e9c"
							target="_blank"
							rel="noreferrer"
							className="underline"
						>
							See how to connect
						</a>
						.
					</>
				}
			/>

			<SettingsContainer className="mb-0 mt-4 px-0 pb-0">
				{!apps || apps.length === 0 ? (
					<div className="px-4 pb-4 text-sm text-secondary">
						No connected apps
					</div>
				) : (
					<div className="flex flex-col">
						<div className="mb-2 flex items-center border-b border-gray-200 px-4 pb-4 text-sm font-medium dark:border-gray-700/80">
							{apps.length} {apps.length === 1 ? "connection" : "connections"}
						</div>
						{apps.map((app) => (
							<ConnectedAppRow
								key={`${app.clientId}:${app.userId}`}
								app={app}
							/>
						))}
					</div>
				)}
			</SettingsContainer>
		</>
	)
}

function ConnectedAppRow({ app }: { app: ConnectedApp }) {
	const ctx = api.useUtils()
	const navigate = useNavigate()
	const [confirmRevoke, setConfirmRevoke] = useState(false)

	const revokeMutation = api.workspace.revokeConnectedApp.useMutation({
		onSuccess: () => {
			toast.success({ title: "Connection revoked" })
			void ctx.workspace.listConnectedApps.invalidate()
		},
	})

	const grantedAgo = formatDistanceToNow(new Date(app.grantedAt), {
		addSuffix: true,
	})
	const lastUsed = app.lastUsedAt
		? `last used ${formatDistanceToNow(new Date(app.lastUsedAt), { addSuffix: true })}`
		: null

	return (
		<>
			<div className="flex items-center gap-3 border-b border-gray-200 px-4 py-4 last:rounded-b-lg last:border-b-0 dark:border-gray-700/80">
				<div className="min-w-0 flex-1">
					<div className="truncate text-sm font-medium">{app.clientName}</div>
					<div className="flex items-center gap-1 truncate text-xs text-secondary">
						Connected by
						<Button
							variant="link"
							onClick={() =>
								void navigate({
									href: `/settings/members?highlight=${app.userId}`,
								})
							}
						>
							{app.userName}
						</Button>
						{grantedAgo}
						{lastUsed ? ` · ${lastUsed}` : ""}
					</div>
				</div>
				<DropdownMenu
					trigger={<IconButton Icon={MoreHorizontal} variant="ghost" />}
				>
					<DropdownMenuItem
						onClick={() => setConfirmRevoke(true)}
						Icon={Trash2}
					>
						Revoke access
					</DropdownMenuItem>
				</DropdownMenu>
			</div>
			<AlertDialog
				isOpen={confirmRevoke}
				onOpenChange={setConfirmRevoke}
				title="Revoke connection?"
				description={`${app.userName}'s connection to "${app.clientName}" will stop working immediately. They can reconnect by authorizing again.`}
				confirmText="Revoke"
				onCancel={() => setConfirmRevoke(false)}
				onConfirm={() => {
					revokeMutation.mutate({
						clientId: app.clientId,
						userId: app.userId,
					})
					setConfirmRevoke(false)
				}}
			/>
		</>
	)
}
